import { useFormik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { addMethod, array, boolean, InferType, object, string } from 'yup';
import { createDrinkItem, deleteDrinkItem, editDrinkItem } from '../../../api/drinkItem';
import {
  createMenuItem,
  deleteMenuItem,
  editMenuItem,
  getMenuItemLocations,
  linkMediaToMenuItem,
  linkModifierGroupsToMenuItem,
  linkRestrictionsToMenuItem,
  linkTagsToMenuItem,
  pairMenuItem
} from '../../../api/menuItem';
import WizardHeader from '../../../components/WizardHeader';
import { PAGE_MENUS } from '../../../constants/UriConstants';
import { useMenuContext } from '../../../contexts/MenuContext';
import { openModal, useModalContext } from '../../../contexts/ModalContext';
import { ItemSizeInterface } from '../../../types/ItemSizeInterface';
import { CreateMenuItemInterface, DetailedMenuItemInterface } from '../../../types/MenuItemInterface';
import WizardProgressBar from '../../../components/WizardProgressBar/WizardProgressBar';
import ItemBasicInfoPage from './ItemBasicInfoPage';
import ItemAdditionalInfoPage from './ItemAdditionalInfoPage';
import { useCharacteristicsContext } from '../../../contexts/CharacteristicsContext';
import { compareArrays, removeAtIndex, replaceAtIndex } from '../../../utils/general';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { useTagsContext } from '../../../contexts/TagsContext';
import { useDrinkItemsContext } from '../../../contexts/DrinkItemsContext';
import { displayLoading, hideLoading, useAsyncContext } from '../../../contexts/AsyncContext';
import { MenuItemMediaInterface } from '../../../types/MediaInterface';
import { TagInterface } from '../../../types/TagInterface';
import { ModifierGroupInterface } from '../../../types/ModifierGroupInterface';
import {
  createModifierGroup,
  deleteModifierGroup,
  editModifierGroup,
  linkModifiersToModifierGroup
} from '../../../api/modifierGroups';
import { PairingInterface } from '../../../types/PairingInterface';
import { DishCharacteristicInterface } from '../../../types/DishCharacteristicInterface';
import { useMediaLibraryContext } from '../../../contexts/MediaLibraryContext';
import { LocationScope } from '../../../types/MenuInterface';
import SelectLocationsModal from '../../../components/SelectLocationsModal';
import useBrandLocations from '../../../hooks/useBrandLocations';

export const MultiPriceSchema = object({
  label: string().default('').required('Size is required.'),
  price: string()
    .default('')
    .required('Price is required.')
    .test('price-is-not-valid', 'Price must be greater than 0.', (value) => {
      const parsed = parseInt(value.replace(/\./, '').replace(/\$/, ''), 10);
      if (Number.isNaN(parsed)) {
        return true;
      }
      return parsed > 0;
    }),
  isCustom: boolean().default(false)
});

addMethod(array, 'unique', function isUnique(message, propertyName, mapper = (a: any) => a) {
  return this.test('unique', message, (list, { createError, path, parent }) => {
    if (!parent?.displayPrices) {
      return true;
    }

    const set = new Set(list.map(mapper));
    if (list.length === set.size) {
      return true;
    }
    const idx = list.findIndex((l, i) => mapper(l) !== Array.from(set)[i]);
    return createError({ path: `${path}.${idx}.${propertyName}`, message });
  });
});

const MenuItemInfoSchema = object({
  displayPrices: boolean().default(false),
  name: string().default('').required('Name is required.'),
  price: array()
    .min(1, 'At least one price is required.')
    .of(MultiPriceSchema)
    .default([MultiPriceSchema.cast({})])
    .unique('Size must be a unique value.', 'label', (a: any) => a.label.toLowerCase()),
  description: string().default(''),
  calories: string().default(''),
  isFeatured: boolean().default(false)
});

interface MenuItemWizardNavState {
  menuItem: CreateMenuItemInterface;
  menuSectionID: number;
  menuID: number;
}

const MenuItemWizard = () => {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const {
    menuItem: { menuItemID: id },
    menuSectionID: selectedMenuSectionID,
    menuID: selectedMenuID
  } = (state as MenuItemWizardNavState) || {
    menuItem: { menuItemID: null },
    menuSectionID: -1,
    menuID: -1
  };

  const { dispatch } = useModalContext();
  const { menu, getMenuItem, addMenuItem, updateMenuItem, removeItem } = useMenuContext();
  const { characteristics } = useCharacteristicsContext();
  const { drinks, addDrink, updateDrink } = useDrinkItemsContext();
  const { menus, currentRestaurant } = useRestaurantContext();
  const { loadTags } = useTagsContext();
  const { dispatch: asyncDispatch } = useAsyncContext();
  const { loadMedia } = useMediaLibraryContext();

  const brandLocations = useBrandLocations();
  const [isSelectLocationsOpen, setIsSelectLocationsOpen] = useState<boolean>(false);
  const [itemLocationIDs, setItemLocationIDs] = useState<number[]>([]);
  // read inside handleOnSubmit, so it has to be set before submitForm runs
  const saveTargetRef = useRef<{ locationScope: LocationScope; restaurantIDs?: number[] }>({
    locationScope: 'single'
  });

  const [menuID, setMenuID] = useState<number>(-1);
  const [menuSectionID, setMenuSectionID] = useState<number>(null);
  const [originalMenuSectionID, setOriginalMenuSectionID] = useState<number>(null);
  const [isPrixFixe, setIsPrixFixe] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<any>(null);

  useEffect(() => {
    // only want to load the lookup list of tags if item wizard is being utilized
    // call to load those tags on initial load
    loadTags();
  }, [loadTags]);

  useEffect(() => {
    // only want to load the restaurants user media if manager page is being utilized
    // call to load media on initial load
    loadMedia();
  }, [loadMedia]);

  useEffect(() => {
    const getInitialMenu = () => {
      const menusWithSections = menus.filter((_menu) => _menu?.menuSections?.length > 0);
      return menusWithSections?.[0];
    };

    if (menu) {
      if (selectedMenuSectionID) {
        setMenuID(selectedMenuID);
        setIsPrixFixe(menu.isPrixFixe);
      } else {
        const initialMenu = getInitialMenu();
        setMenuID(initialMenu.menuID);
        setIsPrixFixe(initialMenu.isPrixFixe);
      }
    }
  }, [menu, menus, selectedMenuID, selectedMenuSectionID]);

  useEffect(() => {
    const getInitialMenuSectionID = () => {
      const menusWithSections = menus.filter((_menu) => _menu?.menuSections?.length > 0);
      return menusWithSections?.[0]?.menuSections?.[0]?.menuSectionID;
    };

    const newMenuSectionID = selectedMenuSectionID || getInitialMenuSectionID();
    if (menuSectionID !== newMenuSectionID && menus && menuSectionID == null) {
      setMenuSectionID(newMenuSectionID);
      setOriginalMenuSectionID(newMenuSectionID);
    }
  }, [menuSectionID, menus, selectedMenuSectionID]);

  const [menuItemID] = useState<number>(id);
  const [menuItem, setMenuItem] = useState<CreateMenuItemInterface>(null);
  const [media, setMedia] = useState<MenuItemMediaInterface[]>([]);

  const [selectedTag, setSelectedTag] = useState<TagInterface>(null);

  const [modifierGroups, setModifierGroups] = useState<ModifierGroupInterface[]>([]);

  const [restrictionIDs, setRestrictionIDs] = useState<number[]>([]);
  const [pairingIDs, setPairingIDs] = useState<number[]>([]);

  const [displayPrices, setDisplayPrices] = useState<boolean>(false);

  const [showRequiredInfoScreen, setShowRequiredInfoScreen] = useState<boolean>(true);

  const [onSubmitError, setOnSubmitError] = useState<string>('');
  const [defaultPriceIndex, setDefaultPriceIndex] = useState<number>(0);

  const isMenuItem = pathname.includes('menu-item');
  const isEditItem = !!menuItemID;

  // when editing, find every location that has a copy of this item (the context copy of the item doesn't include it)
  useEffect(() => {
    if (!isEditItem) return undefined;

    let isCancelled = false;
    getMenuItemLocations(menuItemID)
      .then((locations) => {
        if (!isCancelled) setItemLocationIDs(locations.map((location) => location.restaurantID));
      })
      .catch((error) => {
        // multi-location options just stay hidden; the item itself is loaded from context
        console.error('Error fetching menu item locations:', error);
      });
    return () => {
      isCancelled = true;
    };
  }, [isEditItem, menuItemID]);

  useEffect(() => {
    if (menuItem?.allItemSizes) {
      setDisplayPrices(
        isEditItem &&
          (menuItem.allItemSizes.length > 1 ||
            (menuItem.allItemSizes.length === 1 && menuItem.allItemSizes[0].label !== 'default'))
      );
    }
  }, [menuItem?.allItemSizes, menuItem?.baseItemSize.label, isEditItem]);

  const handleMenuChange = (newMenuID: number, newIsPrixFixe: boolean) => {
    setMenuID(newMenuID);
    setIsPrixFixe(newIsPrixFixe);
  };

  const handleMediaUpdate = (mediaInvolved: MenuItemMediaInterface[]) => {
    setMedia(mediaInvolved);
  };

  const handleOnSubmit = async (value: InferType<typeof MenuItemInfoSchema>) => {
    const parsePrice = (submittedPrice: string, custom: boolean): number => {
      if (custom) {
        return 100;
      }

      return parseInt(submittedPrice.replace(/\D/g, ''), 10);
    };

    const parsePriceOverride = (submittedPrice: string, custom: boolean): string => {
      if (custom) {
        return submittedPrice.trim();
      }

      return '';
    };

    const buildItemSize = (item: InferType<typeof MultiPriceSchema>): ItemSizeInterface => ({
      label: displayPrices ? item.label : 'default',
      price: parsePrice(item.price, item.isCustom),
      priceOverride: parsePriceOverride(item.price, item.isCustom)
    });

    const buildAllItemSizes = (prices: InferType<typeof MultiPriceSchema>[]): ItemSizeInterface[] =>
      prices.map((item) => buildItemSize(item));

    let tag = menuItem?.tags?.[0] || null;
    if (MenuItemInfoSchema.isValidSync(value)) {
      displayLoading({
        dispatch: asyncDispatch,
        message: `${isEditItem ? 'Updating' : 'Creating'} ${isMenuItem ? 'Menu Item' : 'Drink'}...`
      });
      try {
        if (isEditItem) {
          const { name, price } = value;
          const description = value.description || '';
          const calories: number | null =
            value.calories && value.calories !== '' ? parseInt(value.calories.replace(/,/g, ''), 10) : null;
          const prices = price;
          const baseItemSize = buildItemSize(prices[defaultPriceIndex]);
          const allItemSizes = buildAllItemSizes(displayPrices ? prices : [prices[defaultPriceIndex]]);
          const itemToEdit: DetailedMenuItemInterface = {
            name: name?.trim(),
            menuItemID: menuItem.menuItemID,
            description,
            calories,
            category: isMenuItem ? 'food' : 'drink',
            baseItemSize,
            allItemSizes,
            isFeatured: isMenuItem ? value.isFeatured : undefined
          };
          const { locationScope, restaurantIDs } = saveTargetRef.current;
          const editItem = isMenuItem ? editMenuItem : editDrinkItem;
          const result = await editItem(itemToEdit, menuSectionID, locationScope, restaurantIDs);

          // for multi-location saves this item is only edited when its own location was selected
          const isThisItemEdited =
            !result || result.locations.some((location) => location.menuItemID === menuItem.menuItemID);
          const editedCopies =
            result?.locations.filter((location) => location.menuItemID !== menuItem.menuItemID) ?? [];
          const itemLabel = isMenuItem ? 'menu item' : 'drink';

          if (result?.skippedRestaurantIDs?.length) {
            const skippedNames = result.skippedRestaurantIDs.map(
              (restaurantID) =>
                brandLocations.find((location) => location.id === restaurantID)?.label || `Location ${restaurantID}`
            );
            toast.warn(
              `"${name?.trim()}" was not updated at ${skippedNames.join(', ')} because ${
                skippedNames.length > 1 ? 'those locations do' : 'that location does'
              } not have this ${itemLabel}.`,
              { position: toast.POSITION.TOP_RIGHT, autoClose: false }
            );
          }

          const restrictionsChanged = !compareArrays(
            menuItem.dietaryRestrictions.map((val) => val.restrictionID),
            restrictionIDs
          );
          const modifierGroupsChanged = !compareArrays(
            menuItem.modifierGroups.map((val) => val.modifierGroupID),
            modifierGroups.map((val) => val.modifierGroupID),
            true
          );
          const pairingsChanged = !compareArrays(
            menuItem.pairings.map((val) => val.drinkItemID),
            pairingIDs
          );
          const tagChanged = !compareArrays(
            menuItem?.tags?.map((_tag) => _tag?.tagID),
            [selectedTag?.tagID]
          );

          if (!isThisItemEdited) {
            // this location wasn't selected: its item keeps its old values, so only the copies are touched
            try {
              // eslint-disable-next-line no-restricted-syntax
              for (const copy of editedCopies) {
                // eslint-disable-next-line no-await-in-loop
                await linkRestrictionsToMenuItem(copy.menuItemID, restrictionIDs, copy.restaurantID);
              }
            } catch (error) {
              console.log(error);
              openModal({
                dispatch,
                title: `Error occurred while updating ${itemLabel}.`,
                message: `The ${itemLabel} was updated at the selected locations but an issue occurred while updating its dish characteristics. If this issue keeps occurring please reach out to the TapTab team.`,
                onDismiss: () => navigate(`/${currentRestaurant?.restaurantID}/${PAGE_MENUS}`)
              });
              return;
            }
            if (modifierGroupsChanged || pairingsChanged || tagChanged || media !== menuItem.media) {
              toast.info(
                "Tags, modifier groups, pairings and images were not updated because this location wasn't selected.",
                { position: toast.POSITION.TOP_RIGHT, autoClose: false }
              );
            }
            navigate(`/${currentRestaurant?.restaurantID}/${PAGE_MENUS}`);
            return;
          }

          // tags, modifier groups and pairings belong to one location, so only this location's item gets them
          if (editedCopies.length > 0 && (modifierGroupsChanged || pairingsChanged || tagChanged)) {
            toast.info(`Tags, modifier groups and pairings were only updated for the ${itemLabel} at this location.`, {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: false
            });
          }

          try {
            if (restrictionsChanged) {
              await linkRestrictionsToMenuItem(menuItem.menuItemID, restrictionIDs);
            }
            // dish characteristics are shared by every location, so every edited copy gets the same set
            // eslint-disable-next-line no-restricted-syntax
            for (const copy of editedCopies) {
              // eslint-disable-next-line no-await-in-loop
              await linkRestrictionsToMenuItem(copy.menuItemID, restrictionIDs, copy.restaurantID);
            }

            if (modifierGroupsChanged) {
              await linkModifierGroupsToMenuItem(
                menuItem.menuItemID,
                modifierGroups.map((group) => group.modifierGroupID)
              );
            }

            if (pairingsChanged) {
              await pairMenuItem(menuItem.menuItemID, pairingIDs);
            }

            if (tagChanged) {
              await linkTagsToMenuItem(menuItem.menuItemID, selectedTag == null ? [] : [selectedTag?.tagID]);
              tag = selectedTag;
            }
          } catch (error) {
            console.log(error);
            openModal({
              dispatch,
              title: `Error occurred while updating ${isMenuItem ? 'menu item' : 'drink'}.`,
              message: `Unexpected error occurred while updating ${
                isMenuItem ? 'menu item' : 'drink'
              }. Check your input and try again. If this issue keeps occurring please reach out to the TapTab team.`
            });
            return;
          }

          try {
            const mediaIDs: number[] = [];
            const thumbnails: { videoID: number; thumbnailID: number }[] = [];
            for (let i = 0; i < media.length; i++) {
              mediaIDs.push(media[i].mediaID);

              if (media[i].type === 'video') {
                thumbnails.push({
                  videoID: media[i].mediaID,
                  thumbnailID: media[i].thumbnail.thumbnailID
                });
              }
            }
            // media follows every edited copy, including removals; the backend copies it into each location's Media Library
            await linkMediaToMenuItem(menuItem.menuItemID, mediaIDs, thumbnails, editedCopies);
          } catch (error) {
            let errorMessage = `The ${
              isMenuItem ? 'menu item' : 'drink item'
            } was successfully updated but an issue occurred while updating the item's image${
              editedCopies.length > 0 ? ' at the selected locations' : ''
            }. If this issue keeps occurring please reach out to the TapTab team.`;

            if (error?.response?.status === 413) {
              errorMessage =
                'File size of image exceeds limit of 750 KB. Please check the size of the image and try again.';
            }

            openModal({
              dispatch,
              title: 'Error occurred while updating image.',
              message: errorMessage,
              onDismiss: () => navigate(`/${currentRestaurant?.restaurantID}/${PAGE_MENUS}`)
            });
            return;
          } finally {
            if (menuID === menu.menuID) {
              // TODO: only update if changes
              updateMenuItem(menuItem.menuItemID, menuSectionID, {
                name: name?.trim(),
                calories,
                description,
                baseItemSize,
                allItemSizes,
                isFeatured: value.isFeatured,
                media,
                modifierGroups,
                dietaryRestrictions: characteristics.filter((characteristic) =>
                  restrictionIDs.some((restrictionID) => restrictionID === characteristic.restrictionID)
                ),
                pairings: drinks?.filter(
                  (drink) => pairingIDs.some((drinkItemID) => drinkItemID === drink.drinkItemID) ?? []
                ),
                tags: tag ? [tag] : []
              });
            } else if (isEditItem) {
              // remove item from currently selected menu section
              removeItem(menuItem.menuItemID, originalMenuSectionID, isMenuItem ? 'food' : 'drink');
            }
          }

          if (!isMenuItem) {
            updateDrink({
              drinkItemID: menuItem.menuItemID,
              name,
              isHidden: menuItem.isHidden
            });
          }
        } else {
          const prices: InferType<typeof MultiPriceSchema>[] = value.price;
          const itemToCreate: CreateMenuItemInterface = {
            name: value.name?.trim(),
            menuSectionID,
            calories: value.calories !== '' ? parseInt(value.calories.replace(/,/g, ''), 10) : null,
            description: value.description || '',
            category: isMenuItem ? 'food' : 'drink',
            baseItemSize: buildItemSize(prices[defaultPriceIndex]),
            allItemSizes: buildAllItemSizes(displayPrices ? prices : [value.price[defaultPriceIndex]]),
            isFeatured: isMenuItem ? value.isFeatured : undefined
          };
          const { locationScope, restaurantIDs } = saveTargetRef.current;
          const createItem = isMenuItem ? createMenuItem : createDrinkItem;
          const response = await createItem(itemToCreate, locationScope, restaurantIDs);

          // for multi-location saves, the top-level item belongs to another location when this one
          // wasn't selected, so this location only has an item when it's listed in locations
          const currentRestaurantID = currentRestaurant?.restaurantID;
          const createdItem: CreateMenuItemInterface = response.locations
            ? response.locations.find((location) => location.restaurantID === currentRestaurantID)?.menuItem
            : response;
          const otherLocationCopies =
            response.locations?.filter((location) => location.restaurantID !== currentRestaurantID) ?? [];
          const itemLabel = isMenuItem ? 'menu item' : 'drink';

          if (response.skippedLocations?.length) {
            toast.warn(response.skippedLocations.map((location) => location.message).join(' '), {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: false
            });
          }

          // tags, modifier groups and pairings belong to one location, so only this location's item gets them.
          // Images are copied to every copy, but that goes through this location's item, so it needs one too.
          const hasLocationOnlyDetails = pairingIDs.length > 0 || modifierGroups?.length > 0 || selectedTag != null;
          if (otherLocationCopies.length > 0) {
            if (createdItem && hasLocationOnlyDetails) {
              toast.info(`Tags, modifier groups and pairings were only added to the ${itemLabel} at this location.`, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: false
              });
            } else if (!createdItem && (hasLocationOnlyDetails || media.length > 0)) {
              toast.info(
                "Tags, modifier groups, pairings and images were not added because this location wasn't selected.",
                { position: toast.POSITION.TOP_RIGHT, autoClose: false }
              );
            }
          }

          try {
            // dish characteristics are shared by every location, so every copy gets them
            if (restrictionIDs.length > 0) {
              if (createdItem) {
                await linkRestrictionsToMenuItem(createdItem.menuItemID, restrictionIDs);
              }
              // eslint-disable-next-line no-restricted-syntax
              for (const copy of otherLocationCopies) {
                // eslint-disable-next-line no-await-in-loop
                await linkRestrictionsToMenuItem(copy.menuItem.menuItemID, restrictionIDs, copy.restaurantID);
              }
            }

            if (createdItem) {
              if (pairingIDs.length > 0) {
                await pairMenuItem(createdItem.menuItemID, pairingIDs);
              }

              if (modifierGroups?.length > 0) {
                await linkModifierGroupsToMenuItem(
                  createdItem.menuItemID,
                  modifierGroups.map((group) => group.modifierGroupID)
                );
              }

              if (selectedTag != null) {
                await linkTagsToMenuItem(createdItem.menuItemID, [selectedTag.tagID]);
                tag = selectedTag;
              }
            }
          } catch (error) {
            openModal({
              dispatch,
              title: `Error occurred while creating ${itemLabel}.`,
              message: `Unexpected error occurred while creating ${itemLabel}. Check your input and try again. If this issue keeps occurring please reach out to the TapTab team.`
            });
            // remove every copy, so no location is left with a half-created item
            const cleanUpItem = isMenuItem ? deleteMenuItem : deleteDrinkItem;
            await Promise.all([
              ...(createdItem ? [cleanUpItem(createdItem.menuItemID)] : []),
              ...otherLocationCopies.map((copy) =>
                cleanUpItem(copy.menuItem.menuItemID, copy.restaurantID).catch((cleanUpError) =>
                  console.error('Error removing menu item copy:', cleanUpError)
                )
              )
            ]);
            return;
          }

          if (!createdItem) {
            navigate(`/${currentRestaurant?.restaurantID}/${PAGE_MENUS}`);
            return;
          }

          try {
            const mediaIDs: number[] = [];
            const thumbnails: { videoID: number; thumbnailID: number }[] = [];
            for (let i = 0; i < media.length; i++) {
              mediaIDs.push(media[i].mediaID);

              if (media[i].type === 'video') {
                thumbnails.push({
                  videoID: media[i].mediaID,
                  thumbnailID: media[i].thumbnail.thumbnailID
                });
              }
            }
            // media follows every copy of the item; the backend copies it into each location's Media Library
            await linkMediaToMenuItem(
              createdItem.menuItemID,
              mediaIDs,
              thumbnails,
              mediaIDs.length > 0
                ? otherLocationCopies.map((copy) => ({
                    restaurantID: copy.restaurantID,
                    menuItemID: copy.menuItem.menuItemID
                  }))
                : undefined
            );
          } catch (error) {
            let errorMessage = `The ${
              isMenuItem ? 'menu item' : 'drink item'
            } was successfully created but an issue occurred while uploading the item's image(s)${
              otherLocationCopies.length > 0 ? ' at any of the selected locations' : ''
            }. If this issue keeps occurring please reach out to the TapTab team.`;

            if (error?.response?.status === 413) {
              errorMessage =
                'File size of image exceeds limit of 750 KB. Please check the size of the image and try again.';
            }

            openModal({
              dispatch,
              title: 'Error occurred while uploading image.',
              message: errorMessage,
              onDismiss: () => navigate(`/${currentRestaurant?.restaurantID}/${PAGE_MENUS}`)
            });
            return;
          } finally {
            if (menuID === menu.menuID) {
              addMenuItem({
                ...createdItem,
                isFeatured: value.isFeatured,
                media,
                modifierGroups,
                dietaryRestrictions: characteristics.filter((characteristic) =>
                  restrictionIDs.some((restrictionID) => restrictionID === characteristic.restrictionID)
                ),
                pairings: drinks.filter((drink) => pairingIDs.some((drinkItemID) => drinkItemID === drink.drinkItemID)),
                tags: tag ? [tag] : []
              });
            }
            if (!isMenuItem) {
              addDrink({
                drinkItemID: createdItem.menuItemID,
                name: createdItem.name,
                isHidden: false
              });
            }
          }
        }
        navigate(`/${currentRestaurant?.restaurantID}/${PAGE_MENUS}`);
      } catch (error) {
        hideLoading(asyncDispatch);
        if (error?.response?.status === 409) {
          setOnSubmitError(
            `A ${isMenuItem ? 'menu item' : 'drink'} already exists with the name "${value.name}" ${
              saveTargetRef.current.locationScope !== 'single'
                ? `at one of the selected locations. No items were ${isEditItem ? 'updated' : 'created'}.`
                : 'in the selected menu section.'
            }`
          );
        } else if ([400, 422].includes(error?.response?.status) && error?.response?.data?.errors?.[0]?.message) {
          // e.g. 422 when none of the selected locations has a menu section with this name
          setOnSubmitError(error.response.data.errors[0].message);
        } else if (error?.response) {
          setOnSubmitError('An unexpected error has occurred. Please check your input and try again.');
        } else {
          setOnSubmitError(error?.message);
        }
      } finally {
        hideLoading(asyncDispatch);
      }
    }
  };

  const getPrice = (item: ItemSizeInterface): string => {
    const { price, priceOverride } = item;
    if (priceOverride) {
      return priceOverride;
    }
    return `${(price / 100).toFixed(2)}`;
  };

  const getInitialValues = useCallback(
    (isEdit: boolean) => {
      if (isEdit) {
        const parseItemSizes = (itemSizes: ItemSizeInterface[]): InferType<typeof MultiPriceSchema>[] =>
          itemSizes.map((itemSize) => ({
            label: itemSize.label,
            price: getPrice(itemSize),
            isCustom: !!itemSize.priceOverride
          }));
        return {
          name: menuItem?.name,
          calories: menuItem?.calories != null ? `${menuItem?.calories}` : '',
          description: menuItem?.description || '',
          price: parseItemSizes(menuItem?.allItemSizes ?? []),
          displayPrices: false,
          isFeatured: menuItem?.isFeatured ?? false
        };
      }
      return MenuItemInfoSchema.cast({ price: [{ label: 'default' }] });
    },
    [menuItem]
  );

  useEffect(() => {
    if (menuItem && initialValues === null) {
      setInitialValues(getInitialValues(isEditItem));
    }
  }, [menuItem, getInitialValues, isEditItem, initialValues]);

  useEffect(() => {
    const loadMenuItem = () => {
      if (menus?.length > 0 && isEditItem && menuItemID && !menuItem) {
        const menuItemValues: CreateMenuItemInterface = getMenuItem(menuItemID, selectedMenuSectionID);
        setMenuItem(menuItemValues);
        setPairingIDs(menuItemValues?.pairings.map((val: PairingInterface) => val.drinkItemID));
        setRestrictionIDs(
          menuItemValues?.dietaryRestrictions.map((val: DishCharacteristicInterface) => val.restrictionID)
        );
        setModifierGroups(menuItemValues?.modifierGroups ?? []);
        setMedia(menuItemValues?.media);
        setSelectedTag(menuItemValues?.tags?.[0] || null);
        setDefaultPriceIndex(
          menuItemValues?.allItemSizes?.findIndex(
            (itemSize: ItemSizeInterface) => itemSize.label === menuItemValues?.baseItemSize.label || 0
          )
        );
      }
    };
    if (initialValues === null) {
      loadMenuItem();
    }
  }, [getMenuItem, initialValues, isEditItem, menuItem, menuItemID, menus?.length, selectedMenuSectionID]);

  const formik = useFormik({
    initialValues: initialValues || getInitialValues(false),
    validationSchema: MenuItemInfoSchema,
    enableReinitialize: true,
    onSubmit: handleOnSubmit
  });

  const handleNextClicked = () => {
    // Remove whitespace from description field
    const trimmedDescription = formik.values?.description?.trim() || '';
    formik.setFieldValue('description', trimmedDescription);

    const filteredPrices = formik.values?.price?.filter(
      (_price: InferType<typeof MultiPriceSchema>) => _price.label.trim() !== '' || _price.price.trim() !== ''
    );
    if (filteredPrices.length !== 0 && filteredPrices.length !== formik.values?.price?.length) {
      formik.setFieldValue('price', filteredPrices);
    }
    if (isPrixFixe) {
      if (formik.values?.name.trim() !== '') {
        formik.setFieldValue('price', [{ label: 'default', price: 'prix', isCustom: true }]);
        setShowRequiredInfoScreen(false);
      } else if (formik.values?.name.trim() === '') {
        formik.setTouched({ ...formik.touched, name: true });
      }
    } else if (formik.values?.name.trim() !== '' && filteredPrices.length > 0) {
      if (formik.values?.displayPrices) {
        if (defaultPriceIndex + 1 > filteredPrices.length) {
          setDefaultPriceIndex(0);
        }
      }
      if (
        filteredPrices.some(
          (_price: InferType<typeof MultiPriceSchema>) =>
            parseInt(_price.price.replace(/\./, '').replace(/\$/, ''), 10) <= 0
        )
      ) {
        return;
      }
      if (formik.errors.price instanceof string) {
        if (formik.errors.price) {
          return;
        }
      }

      if (formik.errors.price instanceof Array) {
        if (
          formik.errors.price?.some(
            // @ts-ignore
            (_price) => (_price?.price && !_price?.label) || (!_price?.price && _price?.label)
          )
        ) {
          formik.setTouched({
            ...formik.touched,
            name: true,
            price: [...filteredPrices.map(() => ({ price: true, label: true }))]
          });
          return;
        }
      }

      setShowRequiredInfoScreen(false);
    } else {
      formik.setTouched({
        ...formik.touched,
        name: true,
        price:
          filteredPrices?.length === 0
            ? [{ price: true, label: true }]
            : [...filteredPrices.map(() => ({ price: true, label: true }))]
      });
    }
  };

  const handleDisplayOfPrices = () => {
    if (!displayPrices) {
      if (formik.values.price.length === 1 && formik.values.price[0].label === 'default') {
        formik.setFieldValue('price', [
          {
            label: '',
            price: formik.values.price[0].price,
            isCustom: formik.values.price[0].isCustom
          }
        ]);
      }
    } else if (formik.values.price.length === 1 && formik.values.price[0].label === '') {
      // if no label entered for single price in multi view and switching back to single price
      // set the label to default to prevent error.
      formik.setFieldValue(
        'price',
        [
          {
            label: 'default',
            price: formik.values.price[0].price,
            isCustom: formik.values.price[0].isCustom
          }
        ],
        true
      );
      formik.setTouched({
        ...formik.touched,
        price: [{ label: true, price: true }]
      });
    }

    formik.setFieldValue('displayPrices', !displayPrices);
    setDisplayPrices(!displayPrices);
  };

  const handleRemoveSizePrice = (index: number) => {
    if (defaultPriceIndex === index) {
      setDefaultPriceIndex(defaultPriceIndex + 1);
    }
    formik.setFieldValue('price', removeAtIndex(formik.values.price, index));
  };

  const handleAddModifierGroup = async (modifierGroup: Partial<ModifierGroupInterface>) => {
    const createdModifierGroup: ModifierGroupInterface = await createModifierGroup({
      name: modifierGroup.name,
      label: modifierGroup.label,
      modifierIDs: modifierGroup.modifiers.map((modifier) => modifier.modifierID),
      minimumSelections: modifierGroup.minimumSelections,
      maximumSelections: modifierGroup.maximumSelections
    });
    setModifierGroups([...modifierGroups, createdModifierGroup]);
  };

  const handleEditModifierGroup = async (modifierGroup: ModifierGroupInterface) => {
    const idx = modifierGroups.findIndex((group) => group.modifierGroupID === modifierGroup.modifierGroupID);
    const { name, label, modifiers, minimumSelections, maximumSelections } = modifierGroups[idx];

    if (
      name.trim() !== modifierGroup.name.trim() ||
      label.trim() !== modifierGroup.label.trim() ||
      minimumSelections !== modifierGroup.minimumSelections ||
      maximumSelections !== modifierGroup.maximumSelections
    ) {
      await editModifierGroup(
        modifierGroup.modifierGroupID,
        modifierGroup.name,
        modifierGroup.label,
        modifierGroup.minimumSelections,
        modifierGroup.maximumSelections
      );
    }

    const modifierIDs = modifierGroup.modifiers.map((modifier) => modifier.modifierID);
    if (
      !compareArrays(
        modifierIDs,
        modifiers.map((modifier) => modifier.modifierID),
        true
      )
    ) {
      // link modifiers to modifier group
      await linkModifiersToModifierGroup(modifierGroup.modifierGroupID, modifierIDs);
    }

    setModifierGroups(
      replaceAtIndex(modifierGroups, idx, {
        modifierGroupID: modifierGroup.modifierGroupID,
        name: modifierGroup.name,
        label: modifierGroup.label,
        modifiers: modifierGroup.modifiers,
        minimumSelections: modifierGroup.minimumSelections,
        maximumSelections: modifierGroup.maximumSelections
      } as ModifierGroupInterface)
    );
  };

  // a new item can go to any location of the brand; an edit can only reach locations that have a copy of the item
  const selectableLocations = isEditItem
    ? brandLocations.filter((location) => itemLocationIDs.includes(location.id))
    : brandLocations;
  const showLocationOptions = selectableLocations.length > 1;
  const currentRestaurantIDs = currentRestaurant ? [currentRestaurant.restaurantID] : [];

  const saveToLocations = (locationScope: LocationScope, restaurantIDs?: number[]) => {
    saveTargetRef.current = { locationScope, restaurantIDs };
    formik.submitForm();
  };

  const handleSaveOptionSelected = (option: LocationScope) => {
    if (option === 'selected') {
      setIsSelectLocationsOpen(true);
    } else if (option === 'all' && isEditItem) {
      // "all" on the backend means every brand location, which would report the ones without this item as skipped
      saveToLocations(
        'selected',
        selectableLocations.map((location) => location.id)
      );
    } else {
      saveToLocations(option);
    }
  };

  const handleDeleteModifierGroup = async (modifierGroupID: number) => {
    const idx = modifierGroups.findIndex((group) => group.modifierGroupID === modifierGroupID);

    await deleteModifierGroup(modifierGroupID);

    setModifierGroups(removeAtIndex(modifierGroups, idx));
  };

  return (
    <div className="menu-item-wizard-container h-screen w-full flex flex-col items-center overflow-auto">
      <WizardHeader className={`${isMenuItem ? 'menu-item' : 'drink-item'} menu-item-wizard-header`}>
        <div className="menu-item-wizard-header-content">
          <h1>
            {isEditItem ? 'EDIT' : 'CREATE'} {isMenuItem ? 'MENU ITEM' : 'DRINK'}
          </h1>
          <WizardProgressBar
            currentPage={!showRequiredInfoScreen ? 'Additional Info' : 'Basic Info'}
            steps={['Basic Info', 'Additional Info']}
          />
        </div>
      </WizardHeader>
      <form
        className={`menu-item-wizard-content${showRequiredInfoScreen ? '-required' : '-optional'} bg-white`}
        onSubmit={(event) => {
          // with location options, saving goes through SAVE ▾ so the user always picks the locations
          if (showLocationOptions) {
            event.preventDefault();
            return;
          }
          saveTargetRef.current = { locationScope: 'single' };
          formik.handleSubmit(event);
        }}
      >
        {showRequiredInfoScreen ? (
          <ItemBasicInfoPage
            menuID={menuID}
            menuSectionID={menuSectionID}
            isPrixFixe={isPrixFixe}
            defaultIndex={defaultPriceIndex}
            setDefaultIndex={setDefaultPriceIndex}
            formik={formik}
            isMenuItem={isMenuItem}
            showMultiplePrices={displayPrices}
            media={media}
            handleMediaUpdate={handleMediaUpdate}
            handleMenuUpdate={handleMenuChange}
            handleMenuSectionUpdate={(value: number) => setMenuSectionID(value)}
            handleNextClicked={handleNextClicked}
            handleDisplayOfPrices={handleDisplayOfPrices}
            handleRemoveSizePrice={handleRemoveSizePrice}
          />
        ) : (
          <ItemAdditionalInfoPage
            formik={formik}
            itemName={formik.values.name}
            modifierGroups={modifierGroups}
            restrictionIDs={restrictionIDs}
            pairingIDs={pairingIDs}
            selectedTag={selectedTag}
            errorMessage={onSubmitError}
            isMenuItem={isMenuItem}
            handleBackClicked={() => {
              setShowRequiredInfoScreen(true);
              setOnSubmitError('');
            }}
            onCharacteristicsChange={(ids: number[]) => setRestrictionIDs(ids)}
            onPairingsChange={(ids: number[]) => setPairingIDs(ids)}
            onModifierGroupAdd={handleAddModifierGroup}
            onModifierGroupEdit={handleEditModifierGroup}
            onModifierGroupDelete={handleDeleteModifierGroup}
            onModifierGroupsReorder={async (reorderedGroups) => setModifierGroups(reorderedGroups)}
            onSelectedTagChange={(tag: TagInterface) => setSelectedTag(tag)}
            showLocationOptions={showLocationOptions}
            onSaveOptionSelected={handleSaveOptionSelected}
          />
        )}
      </form>
      <SelectLocationsModal
        isOpen={isSelectLocationsOpen}
        locations={selectableLocations}
        initialSelectedIDs={isEditItem ? selectableLocations.map((location) => location.id) : currentRestaurantIDs}
        description={
          isEditItem
            ? `Please select locations where you want to edit this ${isMenuItem ? 'menu item' : 'drink'}.`
            : `Please select locations where you want to save this ${
                isMenuItem ? 'menu item' : 'drink'
              }. At each location it's added to the menu section with the same name.`
        }
        onCancel={() => setIsSelectLocationsOpen(false)}
        onSave={(restaurantIDs) => {
          setIsSelectLocationsOpen(false);
          saveToLocations('selected', restaurantIDs);
        }}
      />
    </div>
  );
};

export default MenuItemWizard;
